import { AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT } from '@govard/ai-chat-kit';
import { z } from 'zod';
import { actionDraftRevisionSnapshotSchema } from '../action-draft.contract.js';

const id = z.string().trim().min(1).max(200);
const version = z.number().int().nonnegative();
export const compoundSelectionBindingSchema = z
  .object({
    draftId: id,
    revisionId: id,
    draftVersion: version,
    workingStateVersion: version,
    contentHash: actionDraftRevisionSnapshotSchema.shape.contentHash,
    sourceHash: actionDraftRevisionSnapshotSchema.shape.contentHash,
  })
  .strict();
export type CompoundSelectionBinding = z.infer<typeof compoundSelectionBindingSchema>;
export const compoundCandidateChoiceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ordinal'), ordinal: z.number().int().min(1).max(8) }).strict(),
  z.object({ kind: z.literal('label'), label: z.string().trim().min(1).max(300) }).strict(),
]);
export type CompoundCandidateChoice = z.infer<typeof compoundCandidateChoiceSchema>;
export const compoundSelectionRequestSchema = z
  .object({ sourceMessageId: id, choice: compoundCandidateChoiceSchema })
  .strict();
export type CompoundSelectionRequest = z.infer<typeof compoundSelectionRequestSchema>;
export const compoundClarificationPayloadSchema = z
  .object({
    type: z.literal('compound_clarification_v1'),
    version: z.literal(1),
    binding: compoundSelectionBindingSchema,
    candidateLabels: z
      .array(z.string().min(1).max(300))
      .min(2)
      .max(AI_CHAT_AMBIGUITY_CANDIDATE_LIMIT),
  })
  .strict();
export type CompoundClarificationPayload = z.infer<typeof compoundClarificationPayloadSchema>;
// A closed, whole-answer vocabulary; no phrase regex or substring matching.
const ordinals = new Map<string, number>([
  ['1', 1],
  ['первую', 1],
  ['первый', 1],
  ['первое', 1],
  ['the first one', 1],
  ['first', 1],
  ['2', 2],
  ['вторую', 2],
  ['второй', 2],
  ['второе', 2],
  ['the second one', 2],
  ['second', 2],
  ['2nd', 2],
  ['3', 3],
  ['третью', 3],
  ['третий', 3],
  ['третье', 3],
  ['the third one', 3],
  ['third', 3],
  ['4', 4],
  ['четвертую', 4],
  ['четвёртую', 4],
  ['the fourth one', 4],
  ['fourth', 4],
  ['5', 5],
  ['пятую', 5],
  ['пятый', 5],
  ['пятое', 5],
  ['the fifth one', 5],
  ['fifth', 5],
  ['6', 6],
  ['7', 7],
  ['8', 8],
]);
export function savedCompoundCandidateChoice(
  text: string,
  labels: readonly string[],
): CompoundCandidateChoice | null {
  const value = text.trim();
  if (labels.includes(value)) return { kind: 'label', label: value };
  const ordinal = ordinals.get(value.toLocaleLowerCase());
  return ordinal === undefined ? null : { kind: 'ordinal', ordinal };
}
export function compoundCandidateOrdinal(
  choice: CompoundCandidateChoice,
  labels: readonly string[],
  originalLabels = labels,
): number | null {
  if (choice.kind === 'ordinal') return choice.ordinal <= labels.length ? choice.ordinal : null;
  const matches = labels.flatMap((label, index) =>
    label === choice.label || originalLabels[index] === choice.label ? [index + 1] : [],
  );
  return matches.length === 1 ? matches[0]! : null;
}
/** Bind to the last assistant actually displayed, never to a refreshed server pointer. */
export function compoundSelectionForMessage(
  messages: readonly { id: string; role: string; structuredPayload?: unknown }[],
  text: string,
): CompoundSelectionRequest | undefined {
  return compoundSelectionFromPlainText(messages, text);
}

/** Plain text may select only an exact saved label or a whole ordinal answer. */
export function compoundSelectionFromPlainText(
  messages: readonly { id: string; role: string; structuredPayload?: unknown }[],
  text: string,
): CompoundSelectionRequest | undefined {
  const last = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant' || message.role === 'user');
  const parsed = compoundClarificationPayloadSchema.safeParse(last?.structuredPayload);
  if (!last || last.role !== 'assistant' || !parsed.success) return undefined;
  const value = text.trim();
  const choice = savedCompoundCandidateChoice(value, parsed.data.candidateLabels);
  if (!choice || (choice.kind === 'label' && !parsed.data.candidateLabels.includes(value)))
    return undefined;
  return { sourceMessageId: last.id, choice };
}
